import { useState, useCallback } from 'react';
import { linkQueueItem, createQueueResource } from './services/queue.service';
import { QUEUE_STATUS } from './constants/lms-course-review.constants';
import useResourceSearchHook from './useResourceSearchHook';

const toResourceOption = (resource) => ({
  id: resource.id ?? resource.resourceKey,
  resourceKey: resource.resourceKey,
  name: resource.name ?? resource.resourceName,
  label: resource.name ?? resource.resourceName,
  externalId: resource.externalId,
  description: resource.description,
  score: resource.score ?? resource.similarityScore,
});

const resolveErrorMessage = (error, actionLabel) => {
  const detail = error?.message || error?.body?.message;
  if (detail && String(detail).toLowerCase().includes('writeback')) {
    return `${actionLabel} failed: Canvas writeback error. The queue item remains pending.`;
  }
  return `${actionLabel} failed. Please try again.`;
};

const useResolutionDialogHook = ({ onResolved, onToast }) => {
  const [open, setOpen] = useState(false);
  const [item, setItem] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [selectedResource, setSelectedResource] = useState(null);
  const [createForm, setCreateForm] = useState({ name: '', externalId: '', description: '' });
  const [actionLoading, setActionLoading] = useState(false);

  const { options: searchOptions, loading: searchLoading } = useResourceSearchHook(searchInput);

  // Use the suggestion already provided by the queue List API. We intentionally
  // do NOT call search-resources to synthesize a suggestion — that endpoint is
  // reserved for explicit manual searches by the admin.
  const applySuggestion = useCallback((queueItem) => {
    setSuggestion(
      queueItem.suggestedResource ? toResourceOption(queueItem.suggestedResource) : null
    );
  }, []);

  const openDialog = useCallback(
    (queueItem) => {
      setItem(queueItem);
      setSuggestion(null);
      setSuggestionDismissed(false);
      setSearchInput('');
      setSelectedResource(null);
      setCreateForm({
        name: queueItem.canvasCourseName || '',
        externalId: queueItem.canvasCourseId || '',
        description: queueItem.description || '',
      });
      setOpen(true);

      if (queueItem.status === QUEUE_STATUS.PENDING) {
        applySuggestion(queueItem);
      }
    },
    [applySuggestion]
  );

  const closeDialog = useCallback(() => {
    setOpen(false);
    setItem(null);
    setSuggestion(null);
    setSuggestionDismissed(false);
    setSearchInput('');
    setSelectedResource(null);
    setCreateForm({ name: '', externalId: '', description: '' });
  }, []);

  const dismissSuggestion = useCallback(() => {
    setSuggestionDismissed(true);
    setSuggestion(null);
  }, []);

  const acceptSuggestion = useCallback(async () => {
    const resourceKey = suggestion?.resourceKey ?? suggestion?.id;
    if (!item || !resourceKey) return;
    setActionLoading(true);
    try {
      await linkQueueItem(item.id, { resourceKey });
      onToast({ type: 'success', message: 'Queue item linked and resolved.' });
      closeDialog();
      onResolved();
    } catch (error) {
      onToast({ type: 'error', message: resolveErrorMessage(error, 'Link') });
    } finally {
      setActionLoading(false);
    }
  }, [item, suggestion, onResolved, onToast, closeDialog]);

  const handleSearchInputChange = useCallback((value) => {
    setSearchInput(value);
    setSelectedResource(null);
  }, []);

  const handleSelectResource = useCallback((option) => {
    setSelectedResource(toResourceOption(option));
  }, []);

  const clearSelectedResource = useCallback(() => {
    setSelectedResource(null);
  }, []);

  const linkSelectedResource = useCallback(async () => {
    const resourceKey = selectedResource?.resourceKey ?? selectedResource?.id;
    if (!item || !resourceKey) return;
    setActionLoading(true);
    try {
      await linkQueueItem(item.id, { resourceKey });
      onToast({ type: 'success', message: 'Queue item linked and resolved.' });
      closeDialog();
      onResolved();
    } catch (error) {
      onToast({ type: 'error', message: resolveErrorMessage(error, 'Link') });
    } finally {
      setActionLoading(false);
    }
  }, [item, selectedResource, onResolved, onToast, closeDialog]);

  const createAndLink = useCallback(async () => {
    if (!item || !createForm.name.trim()) return;
    setActionLoading(true);
    try {
      await createQueueResource(item.id, {
        name: createForm.name.trim(),
        externalId: createForm.externalId.trim() || undefined,
        description: createForm.description.trim() || undefined,
      });
      onToast({ type: 'success', message: 'Course created and queue item resolved.' });
      closeDialog();
      onResolved();
    } catch (error) {
      onToast({ type: 'error', message: resolveErrorMessage(error, 'Create and link') });
    } finally {
      setActionLoading(false);
    }
  }, [item, createForm, onResolved, onToast, closeDialog]);

  const isPending = item?.status === QUEUE_STATUS.PENDING;
  const showSuggestion = Boolean(isPending && suggestion && !suggestionDismissed);

  return {
    dialog: {
      isOpen: open,
      item,
      isPending,
      open: openDialog,
      close: closeDialog,
    },
    suggestion: {
      data: suggestion,
      show: showSuggestion,
      dismiss: dismissSuggestion,
      accept: acceptSuggestion,
    },
    search: {
      input: searchInput,
      setInput: handleSearchInputChange,
      options: searchOptions,
      loading: searchLoading,
      selectedResource,
      select: handleSelectResource,
      clear: clearSelectedResource,
      linkSelected: linkSelectedResource,
    },
    createForm: {
      values: createForm,
      setValues: setCreateForm,
      submit: createAndLink,
    },
    actionLoading,
  };
};

export default useResolutionDialogHook;
